import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  FiFilm, FiCamera, FiMusic, FiGlobe, FiUsers, FiStar,
  FiSend, FiCheckCircle, FiArrowLeft, FiChevronRight,
  FiMonitor, FiUserCheck, FiEdit3,
} from 'react-icons/fi';

// Images
import imgFilm        from '../assets/services/film-production.jpg';
import imgDirection   from '../assets/services/direction-cinematography.jpg';
import imgVfx         from '../assets/services/vfx-post.jpg';
import imgSound       from '../assets/services/score-sound.jpg';
import imgDistrib     from '../assets/services/distribution.jpg';
import imgTalent      from '../assets/services/talent-casting.jpg';
import imgClassDir    from '../assets/services/class-direction.jpg';
import imgClassAct    from '../assets/services/class-acting.jpg';
import imgClassScr    from '../assets/services/class-screenplay.jpg';

// ─── Service Data ─────────────────────────────────────────────────────────────
export const serviceData = {
  'film-production': {
    id: 'film-production',
    icon: FiFilm,
    title: 'Film Production',
    tagline: 'Where Stories Become Legends',
    accent: '#f37021',
    image: imgFilm,
    overview: 'Magic Moon Studios delivers end-to-end feature film production with unmatched precision and artistry. From the spark of an idea to the moment the credits roll, our seasoned team manages every department — scripting, location, casting, shooting, and delivery — ensuring every production is a masterpiece.',
    highlights: [
      'Complete pre-production planning & script development',
      'World-class production management & scheduling',
      'On-location & studio shoot expertise across genres',
      'Budget-conscious production without compromising quality',
      'Post-production coordination & delivery pipeline',
      'Multilingual and multi-platform releases',
    ],
    process: [
      { step: '01', label: 'Concept & Script', desc: 'We develop your story from concept to screen-ready screenplay.' },
      { step: '02', label: 'Pre-Production', desc: 'Casting, scouting, crew assembly, scheduling & budgeting.' },
      { step: '03', label: 'Production', desc: 'Principal photography with cinema-grade equipment and crew.' },
      { step: '04', label: 'Post & Delivery', desc: 'Editing, VFX, sound, colour grade and final delivery.' },
    ],
  },
  'direction-cinematography': {
    id: 'direction-cinematography',
    icon: FiCamera,
    title: 'Direction & Cinematography',
    tagline: 'Every Frame, A Painting',
    accent: '#D4AF37',
    image: imgDirection,
    overview: 'Our award-winning directors and cinematographers bring scripts to life through the power of visual storytelling. We combine technical mastery with creative vision to craft images that linger long after the credits roll — turning every scene into an emotional experience.',
    highlights: [
      'Award-winning directors across genres — drama, thriller, comedy',
      'ARRI, RED & Sony cinema camera expertise',
      'Creative shot design, storyboarding & pre-viz',
      'Drone, underwater & specialty camera operations',
      'Colour-conscious visual language development',
      'On-set and remote direction for international co-productions',
    ],
    process: [
      { step: '01', label: 'Vision Development', desc: 'Collaborative sessions to define visual tone and style.' },
      { step: '02', label: 'Storyboarding', desc: 'Frame-by-frame visual planning for every key scene.' },
      { step: '03', label: 'Principal Photography', desc: 'On-set direction and cinematography execution.' },
      { step: '04', label: 'Look Refinement', desc: 'Final colour tone and visual consistency review.' },
    ],
  },
  'vfx-post': {
    id: 'vfx-post',
    icon: FiStar,
    title: 'VFX & Post Production',
    tagline: 'Realities Beyond Imagination',
    accent: '#f37021',
    image: imgVfx,
    overview: 'Our state-of-the-art post-production pipeline delivers Hollywood-grade visual effects, immersive colour grading, and precision editing. From invisible wire removal to full CGI environments, we transform raw footage into stunning cinematic experiences with industry-leading tools and artists.',
    highlights: [
      'Full CGI environments, creatures & digital humans',
      'Motion capture & performance integration',
      'Professional colour grading with DaVinci Resolve',
      'Dolby Vision & HDR mastering',
      'Multi-platform delivery (theatrical, OTT, broadcast)',
      'Wire removal, crowd replication & matte painting',
    ],
    process: [
      { step: '01', label: 'Editorial', desc: 'Picture edit, assembly cut, director\'s cut and fine cut.' },
      { step: '02', label: 'VFX Shots', desc: 'CGI, compositing, rotoscoping and digital effects.' },
      { step: '03', label: 'Colour Grade', desc: 'Primary and secondary colour grading for final look.' },
      { step: '04', label: 'DCP & Delivery', desc: 'DCP creation, OTT encode and platform delivery.' },
    ],
  },
  'original-score-sound': {
    id: 'original-score-sound',
    icon: FiMusic,
    title: 'Original Score & Sound',
    tagline: 'Music That Moves the Soul',
    accent: '#D4AF37',
    image: imgSound,
    overview: 'Music and sound are the invisible architecture of emotion in cinema. Magic Moon Studios composes bespoke orchestral scores, designs immersive sound environments, and delivers Dolby Atmos audio mixes that transform every scene into a sensory journey. Our composers and sound engineers work alongside directors from day one.',
    highlights: [
      'Bespoke orchestral compositions for feature films',
      'Dolby Atmos & 7.1 surround sound mixing',
      'ADR, Foley & sound effects design',
      'Live recording with full orchestras',
      'Song composition and playback production',
      'Music supervision and licensing support',
    ],
    process: [
      { step: '01', label: 'Spotting Session', desc: 'Scene-by-scene musical and sound design planning.' },
      { step: '02', label: 'Composition', desc: 'Thematic score writing and demo presentations.' },
      { step: '03', label: 'Recording', desc: 'Studio and live orchestra recording sessions.' },
      { step: '04', label: 'Final Mix', desc: 'Dolby Atmos mixing and platform-specific delivery.' },
    ],
  },
  'global-distribution': {
    id: 'global-distribution',
    icon: FiGlobe,
    title: 'Global Distribution',
    tagline: 'Taking Kannada Cinema to the World',
    accent: '#f37021',
    image: imgDistrib,
    overview: 'Magic Moon Studios has built powerful distribution partnerships across theatrical, streaming and international markets. We ensure your film finds its audience — whether in a single-screen in Karnataka or on a global OTT platform with millions of subscribers. Strategic release planning maximises reach and box-office impact.',
    highlights: [
      'Theatrical release planning & marketing strategy',
      'OTT platform partnerships — Netflix, Prime, Zee5, SonyLIV',
      'International sales & festival circuit representation',
      'Dubbed & subtitled versions for 15+ languages',
      'Digital-first release strategies for niche films',
      'P&A (Prints & Advertising) management',
    ],
    process: [
      { step: '01', label: 'Market Assessment', desc: 'Audience analysis and release window planning.' },
      { step: '02', label: 'Distribution Deal', desc: 'Theatrical and OTT deal negotiation and signing.' },
      { step: '03', label: 'Marketing Rollout', desc: 'Trailer, posters, social campaign and press.' },
      { step: '04', label: 'Release & Tracking', desc: 'Box-office monitoring and performance reporting.' },
    ],
  },
  'talent-casting': {
    id: 'talent-casting',
    icon: FiUsers,
    title: 'Talent & Casting',
    tagline: 'The Right Face for Every Role',
    accent: '#D4AF37',
    image: imgTalent,
    overview: 'Great cinema is built on great performances. Magic Moon Studios provides access to an elite network of actors, performers, junior artists and technical talent across South India. Our casting directors match the perfect personality to every role — from leading stars to background performers — with precision and care.',
    highlights: [
      'Access to top Kannada, Tamil, Telugu & Hindi actors',
      'Professional audition coordination & screen tests',
      'Crowd & junior artist management',
      'Contract negotiation & talent coordination',
      'Stunt performers & body-double sourcing',
      'Child artist & senior performer management',
    ],
    process: [
      { step: '01', label: 'Role Breakdown', desc: 'Detailed character profiles and casting requirements.' },
      { step: '02', label: 'Talent Search', desc: 'Network outreach, auditions and shortlisting.' },
      { step: '03', label: 'Screen Tests', desc: 'Camera tests, readings and director approvals.' },
      { step: '04', label: 'Finalisation', desc: 'Contracts, call sheets and talent coordination.' },
    ],
  },
  'class-direction': {
    id: 'class-direction',
    icon: FiMonitor,
    title: 'Direction Masterclass',
    tagline: 'Learn to Lead the Vision',
    accent: '#f37021',
    image: imgClassDir,
    overview: 'Step into the director\'s chair with our comprehensive Direction Masterclass. Designed for aspiring filmmakers, this program covers the entire directing process—from interpreting the script and designing shots, to working with actors and managing a film crew. Learn the secrets of cinematic storytelling from industry veterans.',
    highlights: [
      'Visual storytelling and cinematic language',
      'Directing actors and extracting performances',
      'Camera blocking, framing, and movement techniques',
      'Pre-production planning and storyboarding',
      'On-set leadership and crew management',
      'Hands-on practical directing exercises',
    ],
    process: [
      { step: '01', label: 'Theory & Breakdown', desc: 'Understanding script beats, tone, and visual style.' },
      { step: '02', label: 'Shot Design', desc: 'Creating shot lists, storyboards, and blocking diagrams.' },
      { step: '03', label: 'Working with Actors', desc: 'Techniques for communication and rehearsal.' },
      { step: '04', label: 'Practical Shoot', desc: 'Directing a short scene with a professional crew.' },
    ],
  },
  'class-acting': {
    id: 'class-acting',
    icon: FiUserCheck,
    title: 'Acting Masterclass',
    tagline: 'Unlock Your Authentic Performance',
    accent: '#D4AF37',
    image: imgClassAct,
    overview: 'Our Acting Masterclass offers immersive training for screen and stage. Whether you are a beginner discovering your talent or a professional refining your craft, our expert coaches guide you through emotional truth, character development, and camera-acting techniques to ensure you deliver compelling performances every time.',
    highlights: [
      'Method acting and emotional recall techniques',
      'Voice, diction, and body language training',
      'Acting specifically for the camera vs stage',
      'Improvisation and spontaneous performance',
      'Audition techniques and screen test preparation',
      'Monologue and scene study with professional feedback',
    ],
    process: [
      { step: '01', label: 'Foundations', desc: 'Voice, movement, and breaking down inhibitions.' },
      { step: '02', label: 'Scene Study', desc: 'Analyzing scripts and understanding character motivations.' },
      { step: '03', label: 'On-Camera Technique', desc: 'Adapting performance scale for different shot sizes.' },
      { step: '04', label: 'Showcase', desc: 'Recording a polished monologue or scene for your reel.' },
    ],
  },
  'class-screenplay': {
    id: 'class-screenplay',
    icon: FiEdit3,
    title: 'Screenplay Writing',
    tagline: 'Crafting the Blueprint of Cinema',
    accent: '#f37021',
    image: imgClassScr,
    overview: 'Every great film begins with a great script. The Screenplay Writing Masterclass takes you on a journey from a blank page to a finished draft. Learn the mechanics of story structure, character arcs, dialogue writing, and formatting. You will develop your own original concept under the mentorship of established screenwriters.',
    highlights: [
      'Three-act structure and alternative story paradigms',
      'Creating compelling, multi-dimensional characters',
      'Writing sharp, subtext-driven dialogue',
      'Industry-standard formatting (Final Draft / Celtx)',
      'Pitching techniques and writing treatments',
      'Overcoming writer\'s block and rewriting strategies',
    ],
    process: [
      { step: '01', label: 'Concept & Outline', desc: 'Developing the logline, synopsis, and beat sheet.' },
      { step: '02', label: 'Character Arcs', desc: 'Building character profiles and defining motivations.' },
      { step: '03', label: 'Drafting', desc: 'Writing the first draft, focusing on flow and dialogue.' },
      { step: '04', label: 'Rewriting & Polish', desc: 'Refining the script for pacing, tone, and industry standards.' },
    ],
  },
};

// ─── Enquiry Form ──────────────────────────────────────────────────────────────
function EnquiryForm({ service }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', budget: '', message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const handleChange = (e) =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await addDoc(collection(db, 'serviceEnquiries'), {
        serviceId: service.id,
        serviceName: service.title,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        budget: form.budget,
        message: form.message.trim(),
        createdAt: serverTimestamp(),
      });
      setStatus('success');
      setForm({ name: '', email: '', phone: '', company: '', budget: '', message: '' });
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  const inputClass = `w-full bg-white/5 border border-white/10 focus:border-brand-orange outline-none
    text-white placeholder-white/20 font-inter text-sm px-4 py-3 rounded-sm transition-colors duration-300`;

  const labelClass = 'font-cinzel text-[10px] tracking-widest uppercase block mb-2';

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center text-center py-14 gap-5"
      >
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring' }}
          className="w-20 h-20 rounded-full flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg,#f37021,#D4AF37)' }}
        >
          <FiCheckCircle size={36} color="#0a0a0a" />
        </motion.div>
        <h3 className="font-cinzel font-bold text-white text-2xl">Enquiry Received!</h3>
        <p className="font-inter text-white/50 text-sm max-w-sm leading-relaxed">
          Thank you for your interest in our <strong className="text-brand-gold">{service.title}</strong> service.
          Our team will get back to you within 24 hours.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="btn-royal px-8 py-3 text-xs rounded-sm mt-2"
        >
          Submit Another Enquiry
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={labelClass} style={{ color: `${service.accent}99` }}>Full Name *</label>
          <input type="text" name="name" required value={form.name} onChange={handleChange}
            placeholder="Your Name" className={inputClass} />
        </div>
        <div>
          <label className={labelClass} style={{ color: `${service.accent}99` }}>Email Address *</label>
          <input type="email" name="email" required value={form.email} onChange={handleChange}
            placeholder="your@email.com" className={inputClass} />
        </div>
        <div>
          <label className={labelClass} style={{ color: `${service.accent}99` }}>Phone Number</label>
          <input type="tel" name="phone" value={form.phone} onChange={handleChange}
            placeholder="+91 98765 43210" className={inputClass} />
        </div>
        <div>
          <label className={labelClass} style={{ color: `${service.accent}99` }}>Company / Production House</label>
          <input type="text" name="company" value={form.company} onChange={handleChange}
            placeholder="Your Company" className={inputClass} />
        </div>
      </div>

      <div>
        <label className={labelClass} style={{ color: `${service.accent}99` }}>Estimated Budget</label>
        <select name="budget" value={form.budget} onChange={handleChange} className={inputClass}
          style={{ appearance: 'none' }}>
          <option value="" disabled className="bg-[#1a1a1a] text-white">Select budget range</option>
          <option value="under-10L" className="bg-[#1a1a1a] text-white">Under ₹10 Lakhs</option>
          <option value="10L-50L" className="bg-[#1a1a1a] text-white">₹10L – ₹50 Lakhs</option>
          <option value="50L-1Cr" className="bg-[#1a1a1a] text-white">₹50L – ₹1 Crore</option>
          <option value="1Cr-5Cr" className="bg-[#1a1a1a] text-white">₹1 Cr – ₹5 Crore</option>
          <option value="5Cr+" className="bg-[#1a1a1a] text-white">₹5 Crore+</option>
          <option value="discuss" className="bg-[#1a1a1a] text-white">To be discussed</option>
        </select>
      </div>

      <div>
        <label className={labelClass} style={{ color: `${service.accent}99` }}>Project Details *</label>
        <textarea name="message" required rows={5} value={form.message} onChange={handleChange}
          placeholder={`Tell us about your project and how we can help with ${service.title}...`}
          className={`${inputClass} resize-none`} />
      </div>

      {status === 'error' && (
        <p className="font-inter text-red-400 text-xs text-center">
          Something went wrong. Please try again.
        </p>
      )}

      <motion.button
        type="submit"
        disabled={status === 'loading'}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="btn-royal py-4 text-sm rounded-sm flex items-center justify-center gap-3"
        style={{ opacity: status === 'loading' ? 0.7 : 1 }}
      >
        {status === 'loading' ? (
          <>
            <div className="w-4 h-4 border-2 border-brand-black/40 border-t-brand-black rounded-full animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <FiSend size={16} />
            Submit Enquiry
          </>
        )}
      </motion.button>
    </form>
  );
}

// ─── Main ServiceDetail Page ───────────────────────────────────────────────────
export default function ServiceDetail({ serviceId, onBack }) {
  const service = serviceData[serviceId];

  // Scroll to top when this page is opened or service changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) return null;
  const Icon = service.icon;

  return (
    <div className="relative bg-brand-black min-h-screen">

      {/* ── Hero ── */}
      <div className="relative h-[60vh] sm:h-[70vh] overflow-hidden">
        <motion.img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          style={{ filter: 'brightness(0.45)' }}
        />
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/20 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-black/50 to-transparent" />
        {/* Accent color tint */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at center, ${service.accent}15 0%, transparent 70%)` }} />

        {/* Back button */}
        <motion.button
          onClick={onBack}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="absolute top-28 left-6 z-20 flex items-center gap-2 font-cinzel text-xs tracking-widest uppercase text-white/70 hover:text-brand-gold transition-all duration-300 px-4 py-2 rounded-sm"
          style={{ border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(10,10,10,0.6)', backdropFilter: 'blur(10px)' }}
        >
          <FiArrowLeft size={14} /> Back to Services
        </motion.button>

        {/* Hero content */}
        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}
            className="w-16 h-16 rounded-sm flex items-center justify-center mb-5"
            style={{ background: `linear-gradient(135deg, ${service.accent}33, ${service.accent}11)`, border: `1px solid ${service.accent}50` }}
          >
            <Icon size={28} style={{ color: service.accent }} />
          </motion.div>
          <motion.span className="section-label block mb-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            Magic Moon Studios
          </motion.span>
          <motion.h1
            className="font-cinzel font-black text-4xl sm:text-5xl md:text-6xl text-white mb-3 leading-tight"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
          >
            {service.title}
          </motion.h1>
          <motion.p
            className="font-cinzel text-sm tracking-widest uppercase mb-5"
            style={{ color: service.accent }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
          >
            {service.tagline}
          </motion.p>
          <div className="gold-divider" />
        </motion.div>
      </div>

      {/* ── Overview + Highlights ── */}
      <section className="relative py-16 sm:py-20 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl pointer-events-none"
          style={{ background: `${service.accent}08` }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

            {/* Overview */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span className="section-label block mb-4">Overview</span>
              <h2 className="font-cinzel font-bold text-white text-2xl sm:text-3xl mb-6 leading-snug">
                About Our <span className="text-gold-gradient">{service.title}</span>
              </h2>
              <div className="gold-divider mb-6" />
              <p className="font-inter text-white/60 text-sm leading-relaxed mb-8">
                {service.overview}
              </p>

              {/* Highlights */}
              <div className="space-y-3">
                {service.highlights.map((h, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.07, duration: 0.5 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-5 h-5 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${service.accent}20`, border: `1px solid ${service.accent}40` }}>
                      <FiChevronRight size={10} style={{ color: service.accent }} />
                    </div>
                    <p className="font-inter text-white/70 text-sm">{h}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Process */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span className="section-label block mb-4">Our Process</span>
              <h2 className="font-cinzel font-bold text-white text-2xl sm:text-3xl mb-6 leading-snug">
                How We <span className="text-gold-gradient">Work</span>
              </h2>
              <div className="gold-divider mb-8" />

              <div className="space-y-5">
                {service.process.map((p, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                    className="flex gap-5 p-5 rounded-sm group"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', transition: 'border-color 0.3s' }}
                    whileHover={{ borderColor: `${service.accent}40` }}
                  >
                    <div className="font-cinzel font-black text-3xl flex-shrink-0 leading-none"
                      style={{ color: `${service.accent}30` }}>
                      {p.step}
                    </div>
                    <div>
                      <h4 className="font-cinzel font-bold text-white text-sm mb-1 group-hover:text-brand-gold transition-colors">
                        {p.label}
                      </h4>
                      <p className="font-inter text-white/40 text-xs leading-relaxed">{p.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Enquiry Form ── */}
      <section className="relative py-16 sm:py-20 overflow-hidden"
        style={{ background: 'linear-gradient(180deg,#0a0a0a 0%,#111108 100%)' }}>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold/20 to-transparent" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full blur-3xl pointer-events-none"
          style={{ background: `${service.accent}06` }} />

        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="section-label block mb-4">Enquire Now</span>
            <h2 className="font-cinzel font-black text-3xl sm:text-4xl text-white mb-4">
              Start Your <span className="text-gold-gradient">Project</span>
            </h2>
            <div className="gold-divider mb-4" />
            <p className="font-inter text-white/40 text-sm">
              Interested in our <strong className="text-white/60">{service.title}</strong> service?
              Fill in your details and our team will reach out within 24 hours.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="glass-card p-6 sm:p-10 rounded-sm"
            style={{ border: `1px solid ${service.accent}20` }}
          >
            {/* Corners */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 pointer-events-none rounded-sm"
              style={{ borderColor: `${service.accent}50` }} />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 pointer-events-none rounded-sm"
              style={{ borderColor: `${service.accent}50` }} />
            <EnquiryForm service={service} />
          </motion.div>
        </div>
      </section>

    </div>
  );
}
